/**
 * Local persistence for per-property UI state that has no server-side
 * counterpart yet. Centralised so a future backend can replace these helpers
 * without touching call sites.
 *
 * TODO server: replace with backend endpoints for idle dismissals,
 * rate-history samples, closed-at flags, and pre-qual letter history.
 * (Tours now write through to /tours — see toursStore below — but
 * localStorage stays the source of truth these helpers read from, so a
 * failed write-through never blocks the UI.)
 */

import { rawApi } from '@/apis/rawApi';

const TOURS_KEY = 'unifiedProperty.tours.v1';
const STAGE_ENTERED_KEY = 'unifiedProperty.stageEnteredAt.v1';
const CLOSED_AT_KEY = 'unifiedProperty.closedAt.v1';
const PRE_QUAL_LETTERS_KEY = 'unifiedProperty.preQualLetters.v1';
const RATE_HISTORY_KEY = 'unifiedProperty.rateHistory.v1';
const RATE_ALERTS_KEY = 'unifiedProperty.rateAlerts.v1';
const POST_TOUR_CHECKLIST_KEY = 'unifiedProperty.postTourChecklist.v1';
const IDLE_DISMISSED_KEY = 'unifiedProperty.idleDismissed.v1';
const CD_ACKNOWLEDGED_KEY = 'unifiedProperty.closingDisclosureAcknowledged.v1';
const WIRE_VERIFIED_KEY = 'unifiedProperty.wireInstructionsVerified.v1';
const CASH_READINESS_KEY = 'unifiedProperty.cashReadiness.v1';
const OFFER_ESTIMATE_ACK_KEY = 'unifiedProperty.offerEstimateAcknowledged.v1';

const isBrowser = typeof window !== 'undefined' && Boolean(window.localStorage);

export type LocalPropertyStateKind = 'tours' | 'cashReadiness';

type LocalPropertyStateListener = (
  propertyId: string,
  kind: LocalPropertyStateKind
) => void;

const localPropertyStateListeners = new Set<LocalPropertyStateListener>();

/** Subscribe to client-only property state writes (tours, cash readiness, etc.). */
export function subscribeLocalPropertyState(
  listener: LocalPropertyStateListener
): () => void {
  localPropertyStateListeners.add(listener);
  return () => localPropertyStateListeners.delete(listener);
}

function notifyLocalPropertyState(
  propertyId: string,
  kind: LocalPropertyStateKind
): void {
  for (const listener of localPropertyStateListeners) {
    listener(propertyId, kind);
  }
}

function readJson<T>(key: string, fallback: T): T {
  if (!isBrowser) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded / disabled — silent.
  }
}

export interface TourEntry {
  id: string;
  date: string;
  time: string;
  type: 'in_person' | 'virtual';
  agentName?: string;
  notes?: string;
  loggedAt: string;
  /** Backend Tour._id, filled in once the create write-through succeeds. */
  serverId?: string;
}

/**
 * Best-effort write-through to the backend — localStorage stays authoritative
 * for the UI, this just keeps the admin pipeline's Tours count in sync.
 * Stamps the returned Tour._id back onto the local entry so a later
 * update/remove can target the right backend record.
 */
function syncTourCreate(propertyId: string, entry: TourEntry): void {
  rawApi
    .post('/tours', {
      productId: propertyId,
      date: entry.date,
      time: entry.time,
      type: entry.type,
      agentName: entry.agentName,
      notes: entry.notes,
    })
    .then((res) => {
      const serverId = res.data?._id;
      if (!serverId) return;
      const all = readJson<Record<string, TourEntry[]>>(TOURS_KEY, {});
      const list = (all[propertyId] ?? []).map((t) =>
        t.id === entry.id ? { ...t, serverId } : t
      );
      all[propertyId] = list;
      writeJson(TOURS_KEY, all);
    })
    .catch(() => {
      // Offline/failed sync — local copy still stands, admin pipeline just
      // won't see this tour until the next successful sync.
    });
}

function syncTourUpdate(entry: TourEntry): void {
  if (!entry.serverId) return;
  rawApi
    .put(`/tours/${entry.serverId}`, {
      date: entry.date,
      time: entry.time,
      type: entry.type,
      agentName: entry.agentName,
      notes: entry.notes,
    })
    .catch(() => {
      // Best-effort — see syncTourCreate.
    });
}

function syncTourRemove(serverId: string | undefined): void {
  if (!serverId) return;
  rawApi.delete(`/tours/${serverId}`).catch(() => {
    // Best-effort — see syncTourCreate.
  });
}

export const toursStore = {
  list(propertyId: string): TourEntry[] {
    const all = readJson<Record<string, TourEntry[]>>(TOURS_KEY, {});
    return all[propertyId] ?? [];
  },
  add(propertyId: string, entry: TourEntry): void {
    const all = readJson<Record<string, TourEntry[]>>(TOURS_KEY, {});
    all[propertyId] = [...(all[propertyId] ?? []), entry];
    writeJson(TOURS_KEY, all);
    notifyLocalPropertyState(propertyId, 'tours');
    syncTourCreate(propertyId, entry);
  },
  update(propertyId: string, entry: TourEntry): void {
    const all = readJson<Record<string, TourEntry[]>>(TOURS_KEY, {});
    const list = (all[propertyId] ?? []).map((t) =>
      t.id === entry.id ? entry : t
    );
    all[propertyId] = list;
    writeJson(TOURS_KEY, all);
    notifyLocalPropertyState(propertyId, 'tours');
    syncTourUpdate(entry);
  },
  remove(propertyId: string, entryId: string): void {
    const all = readJson<Record<string, TourEntry[]>>(TOURS_KEY, {});
    const removed = (all[propertyId] ?? []).find((t) => t.id === entryId);
    all[propertyId] = (all[propertyId] ?? []).filter((t) => t.id !== entryId);
    writeJson(TOURS_KEY, all);
    notifyLocalPropertyState(propertyId, 'tours');
    syncTourRemove(removed?.serverId);
  },
};

export const stageEnteredStore = {
  get(propertyId: string, stage: number): string | undefined {
    const all = readJson<Record<string, Record<string, string>>>(
      STAGE_ENTERED_KEY,
      {}
    );
    return all[propertyId]?.[String(stage)];
  },
  set(propertyId: string, stage: number, at: string): void {
    const all = readJson<Record<string, Record<string, string>>>(
      STAGE_ENTERED_KEY,
      {}
    );
    all[propertyId] = { ...(all[propertyId] ?? {}), [String(stage)]: at };
    writeJson(STAGE_ENTERED_KEY, all);
  },
};

export const closedAtStore = {
  get(propertyId: string): string | undefined {
    const all = readJson<Record<string, string>>(CLOSED_AT_KEY, {});
    return all[propertyId];
  },
  set(propertyId: string, at: string): void {
    const all = readJson<Record<string, string>>(CLOSED_AT_KEY, {});
    all[propertyId] = at;
    writeJson(CLOSED_AT_KEY, all);
  },
};

export interface PreQualLetterRecord {
  id: string;
  generatedAt: string;
  borrowerName: string;
  loanAmount: number;
  programLabel: string;
  expiresAt: string;
  /** Object URL — only valid for current tab session. */
  blobUrl?: string;
}

export const preQualLettersStore = {
  list(propertyId: string): PreQualLetterRecord[] {
    const all = readJson<Record<string, PreQualLetterRecord[]>>(
      PRE_QUAL_LETTERS_KEY,
      {}
    );
    return all[propertyId] ?? [];
  },
  add(propertyId: string, record: PreQualLetterRecord): void {
    const all = readJson<Record<string, PreQualLetterRecord[]>>(
      PRE_QUAL_LETTERS_KEY,
      {}
    );
    all[propertyId] = [record, ...(all[propertyId] ?? [])].slice(0, 10);
    writeJson(PRE_QUAL_LETTERS_KEY, all);
  },
};

export interface RateSample {
  /** ISO date (yyyy-MM-dd). */
  date: string;
  rate: number;
}

export const rateHistoryStore = {
  list(key: string): RateSample[] {
    const all = readJson<Record<string, RateSample[]>>(RATE_HISTORY_KEY, {});
    return all[key] ?? [];
  },
  sample(key: string, sample: RateSample, keep = 30): void {
    const all = readJson<Record<string, RateSample[]>>(RATE_HISTORY_KEY, {});
    const list = all[key] ?? [];
    const existingIdx = list.findIndex((s) => s.date === sample.date);
    if (existingIdx >= 0) {
      list[existingIdx] = sample;
    } else {
      list.push(sample);
    }
    all[key] = list.slice(-keep);
    writeJson(RATE_HISTORY_KEY, all);
  },
};

export const rateAlertStore = {
  get(key: string): number | undefined {
    const all = readJson<Record<string, number>>(RATE_ALERTS_KEY, {});
    return all[key];
  },
  set(key: string, threshold: number | null): void {
    const all = readJson<Record<string, number>>(RATE_ALERTS_KEY, {});
    if (threshold === null) {
      delete all[key];
    } else {
      all[key] = threshold;
    }
    writeJson(RATE_ALERTS_KEY, all);
  },
};

export const postTourChecklistStore = {
  get(propertyId: string): Record<string, boolean> {
    const all = readJson<Record<string, Record<string, boolean>>>(
      POST_TOUR_CHECKLIST_KEY,
      {}
    );
    return all[propertyId] ?? {};
  },
  set(propertyId: string, key: string, value: boolean): void {
    const all = readJson<Record<string, Record<string, boolean>>>(
      POST_TOUR_CHECKLIST_KEY,
      {}
    );
    all[propertyId] = { ...(all[propertyId] ?? {}), [key]: value };
    writeJson(POST_TOUR_CHECKLIST_KEY, all);
  },
};

export const idleDismissedStore = {
  has(propertyId: string): boolean {
    const all = readJson<Record<string, boolean>>(IDLE_DISMISSED_KEY, {});
    return Boolean(all[propertyId]);
  },
  dismiss(propertyId: string): void {
    const all = readJson<Record<string, boolean>>(IDLE_DISMISSED_KEY, {});
    all[propertyId] = true;
    writeJson(IDLE_DISMISSED_KEY, all);
  },
};

export const closingDisclosureAcknowledgedStore = {
  get(propertyId: string): boolean {
    const all = readJson<Record<string, boolean>>(CD_ACKNOWLEDGED_KEY, {});
    return Boolean(all[propertyId]);
  },
  set(propertyId: string, value: boolean): void {
    const all = readJson<Record<string, boolean>>(CD_ACKNOWLEDGED_KEY, {});
    if (value) all[propertyId] = true;
    else delete all[propertyId];
    writeJson(CD_ACKNOWLEDGED_KEY, all);
  },
};

export const wireInstructionsVerifiedStore = {
  get(propertyId: string): boolean {
    const all = readJson<Record<string, boolean>>(WIRE_VERIFIED_KEY, {});
    return Boolean(all[propertyId]);
  },
  set(propertyId: string, value: boolean): void {
    const all = readJson<Record<string, boolean>>(WIRE_VERIFIED_KEY, {});
    if (value) all[propertyId] = true;
    else delete all[propertyId];
    writeJson(WIRE_VERIFIED_KEY, all);
  },
};

/** Cash Stage 3 proof-of-funds acknowledgements (no mortgage 1003). */
export const cashReadinessStore = {
  get(propertyId: string): Record<string, boolean> {
    const all = readJson<Record<string, Record<string, boolean>>>(
      CASH_READINESS_KEY,
      {}
    );
    return all[propertyId] ?? {};
  },
  set(propertyId: string, value: Record<string, boolean>): void {
    const all = readJson<Record<string, Record<string, boolean>>>(
      CASH_READINESS_KEY,
      {}
    );
    all[propertyId] = value;
    writeJson(CASH_READINESS_KEY, all);
    notifyLocalPropertyState(propertyId, 'cashReadiness');
  },
  isComplete(propertyId: string): boolean {
    const v = cashReadinessStore.get(propertyId);
    return Boolean(v.funds && v.id && v.title);
  },
};

/**
 * Marks that the buyer has moved past the Offer Estimate stage (no
 * server-side signal exists for this — unlike the Offer Submission stage
 * that follows it, viewing an estimate and comps doesn't create anything).
 * Read by stageResolver to distinguish "tour logged" (stage 4) from
 * "acknowledged the estimate, ready to build an offer" (stage 5).
 */
export const offerEstimateAcknowledgedStore = {
  has(propertyId: string): boolean {
    const all = readJson<Record<string, boolean>>(OFFER_ESTIMATE_ACK_KEY, {});
    return Boolean(all[propertyId]);
  },
  mark(propertyId: string): void {
    const all = readJson<Record<string, boolean>>(OFFER_ESTIMATE_ACK_KEY, {});
    all[propertyId] = true;
    writeJson(OFFER_ESTIMATE_ACK_KEY, all);
  },
};
